<template>
  <Card class="layout-card">
    <div ref="searchBar" class="short-review-search-bar">
    <Form label-position="top" :model="searchForm">
      <Row :gutter="10" type="flex" align="bottom">
        <Col :span="5">
          <FormItem>
            <Input
              v-model="searchForm.keyword"
              placeholder="搜索名称 / 剧目 ID"
              clearable
            />
          </FormItem>
        </Col>
        <Col :span="5">
          <FormItem>
            <Select
              v-model="searchForm.language"
              clearable
              placeholder="请选择主剧语言"
            >
              <Option
                v-for="item in langOptions"
                :key="item.value"
                :value="item.value"
                :label="item.label"
              />
            </Select>
          </FormItem>
        </Col>
         <Col :span="5">
          <FormItem>
            <Select
              v-model="searchForm.syncStatus"
              clearable
              placeholder="请选择 BP 同步状态"
            >
              <Option
                v-for="item in syncStatusOptions"
                :key="item.value"
                :value="item.value"
                :label="item.label"
              />
            </Select>
          </FormItem>
        </Col>
        <Col :span="5">
          <FormItem>
            <Select
              v-model="searchForm.reviewStatus"
              clearable
              placeholder="请选择 TT 审核状态"
            >
              <Option
                v-for="item in reviewStatusOptions"
                :key="item.value"
                :value="item.value"
                :label="item.label"
              />
            </Select>
          </FormItem>
        </Col>
        <Col :span="4">
          <FormItem>
            <Button type="primary" icon="ios-search" @click="handleSearch">搜索</Button>
            <Button style="margin-left: 10px" @click="handleReset">重置</Button>
          </FormItem>
        </Col>
      </Row>
    </Form>
    </div>

     <div class="review-tip-bar">
      仅支持无字幕原片 + 外挂字幕送审；英语外挂字幕必需，其他字幕不独立送审。
    </div>
    <Table
      class="short-review-table"
      :data="tableData"
      :columns="columns"
      :loading="loading"
      style="margin-top: 10px"
    >
      <template #dramaInfo="{ row }">
        <div class="drama-info-cell">
          <div class="drama-cover">
            <img
              v-if="row.coverUrl"
              :src="row.coverUrl"
              alt="封面"
              @click.stop="handlePreviewCover(row)"
            />
            <span v-else class="drama-cover-placeholder">封面</span>
          </div>
          <div class="drama-text">
            <div class="drama-title-en">{{ row.playName }}</div>
            <div class="drama-title-cn">{{ row.chineseName }}</div>
          </div>
        </div>
      </template>

       <template #bpSyncSlot="{ row }">
        <div class="bp-sync-cell">
          <div class="bp-sync-line">
            <span
              class="status-dot"
              :class="getBpSyncDotClass(row.bpSyncStatus)"
            />
            <span class="bp-sync-text">{{ row.bpSyncText }}</span>
            <span
              v-if="row.bpSyncStatus === 'failed'"
              class="bp-sync-fail-help"
              title="查看同步失败详情"
              @click.stop="handleOpenSyncFailDialog(row)"
            >?</span>
          </div>
          <div
            v-if="row.bpSyncTime && row.bpSyncStatus !== 'new'"
            class="status-time bp-sync-time"
          >
            {{ row.bpSyncTime }}
          </div>
        </div>
      </template>
      <template #reviewStatusSlot="{ row }">
        <div class="status-block">
          <div
            class="review-status-badge"
            :style="getReviewBadgeStyle(row.reviewStatus)"
          >
            {{ row.reviewText }}
          </div>
          <div v-if="row.reviewMissingHint" class="review-submit-missing-hint">
            {{ row.reviewMissingHint }}
          </div>
          <div v-else-if="row.reviewTime" class="status-time">{{ row.reviewTime }}</div>
        </div>
      </template>
      <template #actionSlot="{ row }">
        <div class="action-links">
          <a @click="handleViewInfo(row)">查看资料</a>
          <a
            v-if="showHandleRejectionAction(row)"
            @click="handleProcessRejection(row)"
          >
            处理驳回
          </a>
          <a @click="handleViewRecords(row)">审核记录</a>
          <a @click="handlePreview(row)">在线预览</a>
        </div>
      </template>

      <template #footer>
        <Pages :page="page" :func="handleGetData" :show-sizer="false" />
      </template>
    </Table>

    <ViewProfileDialog
      :visible.sync="profileDialogVisible"
      :row="profileDialogRow"
      :default-edit-mode="profileDefaultEditMode"
      @close="profileDefaultEditMode = false"
      @submit="handleProfileSubmitSuccess"
    />

    <ReviewRecordDialog
      :visible.sync="reviewRecordVisible"
      :row="reviewRecordRow"
      :actions-disabled="!!reviewRecordActionsUsedMap[reviewRecordRow.id]"
      :appeal-allowed="canInitiateAppeal(reviewRecordRow)"
      @modify-submit="handleReviewRecordModifySubmit"
      @open-appeal="handleOpenAppealFromRecord"
      @withdraw-success="handleWithdrawSuccess"
    />

    <BpSyncFailDialog
      :visible.sync="syncFailDialogVisible"
      :row="syncFailDialogRow"
    />

    <OverRuleDialog
      :visible.sync="overRuleVisible"
      :row="overRuleRow"
      @modify-submit="handleOverRuleModifySubmit"
      @open-appeal="handleOpenAppeal"
    />

    <AppealDialog
      :visible.sync="appealVisible"
      :row="appealRow"
      @success="handleAppealSuccess"
      @close="handleAppealClose"
      @dismiss="handleAppealDismiss"
    />

    <PreviewDialog
      :visible.sync="previewDialogVisible"
      :row="previewDialogRow"
    />
  </Card>
</template>

<script>
import dayjs from 'dayjs'
import Pages from '@/components/pages'
import { getLangList } from '@/api/lang'
import { getTtMediaReviewPage } from '@/api/short-review'
import ViewProfileDialog from './components/ViewProfileDialog/index.vue'
import ReviewRecordDialog from './components/ReviewRecordDialog/index.vue'
import BpSyncFailDialog from './components/BpSyncFailDialog/index.vue'
import OverRuleDialog from './components/OverRuleDialog/index.vue'
import AppealDialog from './components/AppealDialog/index.vue'
import PreviewDialog from './components/PreviewDialog/index.vue'
import { getListReviewMissingHint } from './utils/submitPrecheck'
import {
  canInitiateAppeal,
  REVIEW_STATUS_AFTER_APPEAL_SUBMIT
} from './utils/appeal'
import {
  REVIEW_REJECT_LABEL_BG,
  REVIEW_REJECT_LABEL_BORDER,
  REVIEW_REJECT_LABEL_COLOR
} from './utils/reviewTheme'

/**
 * 列表行 reviewStatus 明细（接口返回 0–7）→ 状态标签文案
 * 0草稿 1审核中 2通过 3拒绝 4撤回 5申诉中 6申诉通过 7申诉拒绝
 */
const REVIEW_STATUS_UI = {
  0: { key: 'pending', text: '未送审' },
  1: { key: 'reviewing', textPrefix: '审核中' },
  2: { key: 'passed', textPrefix: '审核通过' },
  3: { key: 'rejected', textPrefix: '审核驳回' },
  4: { key: 'withdrawn', textPrefix: '已撤回' },
  5: { key: 'appealing', textPrefix: '已驳回-申诉中' },
  6: { key: 'passed', textPrefix: '审核通过（申诉通过）' },
  7: { key: 'rejected', textPrefix: '已驳回-申诉未通过' }
}

/**
 * 操作列：以下审核明细状态展示「处理驳回」（默认仅 查看资料 / 审核记录 / 在线预览）
 * 3 审核驳回、5 已驳回-申诉中、7 已驳回-申诉未通过
 */
const REVIEW_STATUS_SHOW_HANDLE_REJECTION = [3, 5, 7]

/**
 * 筛选用 reviewStatus（query 传 0–4）
 * 2 审核通过：结果含明细 2、6
 * 3 审核驳回：结果含明细 3、5、7
 */
const REVIEW_STATUS_FILTER_OPTIONS = [
  { value: 0, label: '未送审' },
  { value: 1, label: '审核中' },
  { value: 2, label: '审核通过' },
  { value: 3, label: '审核驳回' },
  { value: 4, label: '已撤回' }
]

/** 分页：固定每页条数，不可切换 page-size */
const FIXED_PAGE_SIZE = 10

/** BP 同步状态仅四种（与筛选项、列表展示一致） */
const SYNC_STATUS_UI = {
  NEW: { key: 'new', text: '尚未同步' },
  SYNCING: { key: 'syncing', text: '同步中' },
  READY: { key: 'ready', text: '同步成功' },
  FAILED: { key: 'failed', text: '同步失败' }
}

// 各列基础宽度；表格比基础总宽更宽时，按该比例把余量分给六列（避免只撑「主剧信息」）
const COLUMN_WIDTH_BASE = {
  mainPlayId: 100,
  playName: 220,
  langName: 110,
  bpSyncText: 140,
  reviewText: 140,
  action: 110
}

const createColumns = (widths) => [
  {
    title: '剧目 ID',
    key: 'mainPlayId',
    width: widths.mainPlayId
  },
  {
    title: '主剧信息',
    key: 'playName',
    width: widths.playName,
    slot: 'dramaInfo'
  },
  {
    title: '主剧语言',
    key: 'langName',
    width: widths.langName
  },
  {
    title: 'BP 空间同步',
    key: 'bpSyncText',
    width: widths.bpSyncText,
    slot: 'bpSyncSlot'
  },
  {
    title: '审核状态',
    key: 'reviewText',
    width: widths.reviewText,
    slot: 'reviewStatusSlot'
  },
  {
    title: '操作',
    key: 'action',
    width: widths.action,
    slot: 'actionSlot'
  }
]

export default {
  name: 'ShortReview',
  components: {
    Pages,
    ViewProfileDialog,
    ReviewRecordDialog,
    BpSyncFailDialog,
    OverRuleDialog,
    AppealDialog,
    PreviewDialog
  },
  data() {
    return {
      appealVisible: false,
      appealRow: {},
      /** 从审核记录进入申诉时，取消申诉需回到审核记录弹窗 */
      appealReturnToReviewRecord: false,
      /** 从处理驳回进入申诉时，取消申诉需回到驳回弹窗 */
      appealReturnToOverRule: false,
      overRuleVisible: false,
      overRuleRow: {},
      profileDialogVisible: false,
      profileDialogRow: {},
      profileDefaultEditMode: false,
      reviewRecordVisible: false,
      reviewRecordRow: {},
      syncFailDialogVisible: false,
      syncFailDialogRow: {},
      previewDialogVisible: false,
      previewDialogRow: {},
      /** 审核记录 id（接口 path 用 id，非 mainPlayId）-> 已操作修改/申诉 */
      reviewRecordActionsUsedMap: {},
      loading: false,
      tableData: [],
      page: {
        current: 1,
        size: FIXED_PAGE_SIZE,
        total: 0
      },
      searchForm: {
        keyword: '',
        language: '',
        syncStatus: '',
        reviewStatus: ''
      },
      langOptions: [],
      syncStatusOptions: [
        { value: 'NEW', label: '尚未同步' },
        { value: 'SYNCING', label: '同步中' },
        { value: 'READY', label: '同步成功' },
        { value: 'FAILED', label: '同步失败' }
      ],
      reviewStatusOptions: REVIEW_STATUS_FILTER_OPTIONS,
      columns: createColumns(COLUMN_WIDTH_BASE)
    }
  },
  mounted() {
    this.loadLanguageOptions()
    this.handleGetData()
    this.bindSearchEnterKey()
    this.$nextTick(() => {
      this.updateColumnWidths()
      this._onTableResize = () => this.updateColumnWidths()
      window.addEventListener('resize', this._onTableResize)
    })
  },
  beforeDestroy() {
    this.unbindSearchEnterKey()
    if (this._onTableResize) {
      window.removeEventListener('resize', this._onTableResize)
    }
  },
  methods: {
    updateColumnWidths() {
      const root = this.$el && this.$el.querySelector('.short-review-table')
      if (!root) return
      const tableWidth = root.clientWidth
      if (!tableWidth) return

      const keys = Object.keys(COLUMN_WIDTH_BASE)
      const baseSum = keys.reduce((sum, key) => sum + COLUMN_WIDTH_BASE[key], 0)
      const extra = Math.max(0, tableWidth - baseSum)
      const widths = { ...COLUMN_WIDTH_BASE }
      let distributed = 0

      keys.forEach((key, index) => {
        if (index === keys.length - 1) {
          widths[key] += extra - distributed
          return
        }
        const add = Math.floor(extra * (COLUMN_WIDTH_BASE[key] / baseSum))
        widths[key] += add
        distributed += add
      })

      keys.forEach((key) => {
        const col = this.columns.find((item) => item.key === key)
        if (col && col.width !== widths[key]) {
          this.$set(col, 'width', widths[key])
        }
      })
    },
    loadLanguageOptions() {
      getLangList({ current: -1, size: -1, langStatus: true }).then((res) => {
        if (res.code !== 200) return
        const records = (res.data && res.data.records) || []
        this.langOptions = records
          .filter((item) => item.langCode)
          .map((item) => ({
            value: item.langCode,
            label: item.chineseLangName || item.langName || item.langCode
          }))
      })
    },
    buildListQueryParams() {
      const params = {
        current: this.page.current,
        size: FIXED_PAGE_SIZE,
        name: (this.searchForm.keyword || '').trim(),
        language: this.searchForm.language,
        syncStatus: this.searchForm.syncStatus,
        reviewStatus: this.searchForm.reviewStatus
      }
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] == null) {
          delete params[key]
        }
      })
      if (params.reviewStatus !== undefined) {
        params.reviewStatus = Number(params.reviewStatus)
      }
      return params
    },
    formatShortTime(time) {
      if (!time) return ''
      const parsed = dayjs(time)
      return parsed.isValid() ? parsed.format('MM/DD HH:mm') : ''
    },
    formatReviewVersionText(record, ui) {
      if (ui.text) return ui.text
      const version = record.reviewTtVersion
      const prefix = version != null && version > 0 ? `V${version} ` : ''
      return `${prefix}${ui.textPrefix || ''}`
    },
    formatTableRow(record) {
      const reviewUi =
        REVIEW_STATUS_UI[record.reviewStatus] || REVIEW_STATUS_UI[0]
      let syncKey = (record.syncStatus || '').toUpperCase()
      if (syncKey === 'MISSING') {
        syncKey = 'FAILED'
      }
      const syncUi = SYNC_STATUS_UI[syncKey] || SYNC_STATUS_UI.NEW
      const bpSyncText = syncUi.text
      return {
        ...record,
        langName: record.languageName || record.languageCode || '—',
        bpSyncStatus: syncUi.key,
        bpSyncText,
        bpSyncTime: this.formatShortTime(record.lastSyncAt),
        bpSyncTimeRaw: record.lastSyncAt,
        reviewStatus: reviewUi.key,
        reviewStatusApi: record.reviewStatus,
        reviewText: this.formatReviewVersionText(record, reviewUi),
        reviewTime: this.formatShortTime(record.reviewUpdatedAt),
        reviewMissingHint: getListReviewMissingHint(record)
      }
    },
    /**
     * 拉列表：搜索/重置传 current=1；翻页传页码。打开弹窗返回不调用本方法以保留筛选与页码。
     * 默认排序由后端按 reviewUpdatedAt 倒序。
     */
    handleGetData(current) {
      this.loading = true
      if (typeof current === 'number' && current >= 1) {
        this.page.current = current
      } else {
        this.page.current = 1
      }
      this.page.size = FIXED_PAGE_SIZE
      getTtMediaReviewPage(this.buildListQueryParams())
        .then((res) => {
          if (res.code === 200 && res.data) {
            const records = res.data.records || []
            this.tableData = records.map((item) => this.formatTableRow(item))
            this.page.total = res.data.total != null ? res.data.total : 0
            if (res.data.current != null) {
              this.page.current = res.data.current
            }
          } else {
            this.$Message.error(res.message || '获取审核列表失败')
            this.tableData = []
            this.page.total = 0
          }
        })
        .catch(() => {
          this.$Message.error('获取审核列表失败')
          this.tableData = []
          this.page.total = 0
        })
        .finally(() => {
          this.loading = false
          this.syncOpenDialogRowsFromTable()
        })
    },
    /** 列表刷新后，仍打开的弹窗使用同一审核记录的最新行数据 */
    syncOpenDialogRowsFromTable() {
      const pickRow = (id) =>
        this.tableData.find((item) => item.id === id) || null
      if (this.profileDialogVisible && this.profileDialogRow) {
        const id = this.profileDialogRow.id
        const row = id != null ? pickRow(id) : null
        if (row) {
          this.profileDialogRow = { ...row }
        }
      }
      if (this.reviewRecordVisible && this.reviewRecordRow) {
        const id = this.reviewRecordRow.id
        const row = id != null ? pickRow(id) : null
        if (row) {
          this.reviewRecordRow = { ...row }
        }
      }
    },
    /** 修改送审资料 / 撤回等操作成功后刷新列表（保留当前页与筛选） */
    refreshReviewListAfterMutation() {
      this.handleGetData(this.page.current)
    },
    handleSearch() {
      this.handleGetData(1)
    },
    bindSearchEnterKey() {
      this.unbindSearchEnterKey()
      this._onSearchEnterKeydown = (e) => {
        if (!e || (e.key !== 'Enter' && e.keyCode !== 13)) return
        const bar = this.$refs.searchBar
        if (!bar || !bar.contains(e.target)) return
        if (this.shouldIgnoreSearchEnter(e)) return
        e.preventDefault()
        this.handleSearch()
      }
      document.addEventListener('keydown', this._onSearchEnterKeydown)
    },
    unbindSearchEnterKey() {
      if (this._onSearchEnterKeydown) {
        document.removeEventListener('keydown', this._onSearchEnterKeydown)
        this._onSearchEnterKeydown = null
      }
    },
    /** 按钮自身 Enter、下拉展开时不触发搜索 */
    shouldIgnoreSearchEnter(e) {
      const el = e.target
      if (!el) return true
      if (el.tagName === 'BUTTON') return true
      if (el.closest && el.closest('.ivu-select-dropdown')) return true
      return Array.from(document.querySelectorAll('.ivu-select-dropdown')).some(
        (node) => {
          const rect = node.getBoundingClientRect()
          return rect.width > 0 && rect.height > 0
        }
      )
    },
    handleReset() {
      this.searchForm = {
        keyword: '',
        language: '',
        syncStatus: '',
        reviewStatus: ''
      }
      this.handleGetData(1)
    },
    getBpSyncDotClass(status) {
      const map = {
        syncing: 'status-dot--blue',
        ready: 'status-dot--green',
        new: 'status-dot--gray',
        failed: 'status-dot--red'
      }
      return map[status] || 'status-dot--gray'
    },
    /** 默认 3 项；审核驳回类状态增加「处理驳回」（见 REVIEW_STATUS_SHOW_HANDLE_REJECTION） */
    showHandleRejectionAction(row) {
      const code = row.reviewStatusApi
      return REVIEW_STATUS_SHOW_HANDLE_REJECTION.includes(code)
    },
    getReviewBadgeStyle(status) {
      const styleMap = {
        reviewing: {
          backgroundColor: '#ecf5ff',
          color: '#409eff',
          borderColor: '#b3d8ff'
        },
        appealing: {
          backgroundColor: REVIEW_REJECT_LABEL_BG,
          color: REVIEW_REJECT_LABEL_COLOR,
          borderColor: REVIEW_REJECT_LABEL_BORDER
        },
        pending: {
          backgroundColor: '#f4f4f5',
          color: '#909399',
          borderColor: '#e4e7ed'
        },
        passed: {
          backgroundColor: '#f0f9eb',
          color: '#67c23a',
          borderColor: '#c2e7b0'
        },
        rejected: {
          backgroundColor: REVIEW_REJECT_LABEL_BG,
          color: REVIEW_REJECT_LABEL_COLOR,
          borderColor: REVIEW_REJECT_LABEL_BORDER
        },
        withdrawn: {
          backgroundColor: '#f4f4f5',
          color: '#909399',
          borderColor: '#e4e7ed'
        }
      }
      const current = styleMap[status] || styleMap.pending
      const base = {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        lineHeight: '20px',
        border: '1px solid',
        borderColor: current.borderColor,
        backgroundColor: current.backgroundColor,
        color: current.color
      }
      return base
    },
    handleOpenSyncFailDialog(row) {
      this.syncFailDialogRow = { ...row }
      this.syncFailDialogVisible = true
    },
    handlePreviewCover(row) {
      const url = (row && row.coverUrl && String(row.coverUrl).trim()) || ''
      if (!url) {
        this.$Message.info('暂无封面')
        return
      }
      this.$viewerApi({ images: [url] })
    },
    handleViewInfo(row) {
      this.profileDialogRow = { ...row }
      this.profileDialogVisible = true
    },
    handleProcessRejection(row) {
      this.overRuleRow = { ...row }
      this.overRuleVisible = true
    },
    handleOverRuleModifySubmit(row) {
      this.overRuleVisible = false
      this.profileDialogRow = { ...row }
      this.profileDefaultEditMode = true
      this.profileDialogVisible = true
    },
    handleOpenAppeal(row) {
      if (!canInitiateAppeal(row)) {
        this.$Message.warning('该剧已发起过申诉或当前状态不可申诉')
        return
      }
      this.appealReturnToReviewRecord = false
      this.appealReturnToOverRule = true
      this.overRuleVisible = false
      this.appealRow = { ...row }
      this.appealVisible = true
    },
    /** 从审核记录弹窗内打开申诉（先关闭记录弹窗，取消时可回到记录） */
    handleOpenAppealFromRecord(row) {
      if (!canInitiateAppeal(row)) {
        this.$Message.warning('该剧已发起过申诉或当前状态不可申诉')
        return
      }
      this.appealReturnToOverRule = false
      this.appealReturnToReviewRecord = true
      this.appealRow = { ...row }
      this.appealVisible = true
    },
    /** 申诉弹窗右上角 ×：清除返回标记，不 reopen 上级弹窗 */
    handleAppealDismiss() {
      this.appealReturnToReviewRecord = false
      this.appealReturnToOverRule = false
    },
    /** 申诉弹窗底部「取消」：回到驳回或审核记录 */
    handleAppealClose() {
      if (this.appealReturnToReviewRecord) {
        this.appealReturnToReviewRecord = false
        this.$nextTick(() => {
          this.reviewRecordVisible = true
        })
        return
      }
      if (this.appealReturnToOverRule) {
        this.appealReturnToOverRule = false
        this.$nextTick(() => {
          this.overRuleVisible = true
        })
      }
    },
    /** 申诉提交成功后，列表审核状态更新为「已驳回-申诉中」 */
    patchTableRowReviewStatus(dramaId, reviewStatusApi) {
      if (dramaId == null) return
      const idx = this.tableData.findIndex((item) => item.id === dramaId)
      if (idx < 0) return
      const source = this.tableData[idx]
      const patched = {
        ...source,
        reviewStatus: reviewStatusApi,
        reviewStatusApi,
        reviewUpdatedAt: new Date().toISOString()
      }
      this.$set(this.tableData, idx, this.formatTableRow(patched))
    },
    handleAppealSuccess(row) {
      const id = row && row.id
      if (id == null) return
      this.appealReturnToReviewRecord = false
      this.appealReturnToOverRule = false
      this.markReviewRecordActionsDisabled(id)
      this.patchTableRowReviewStatus(id, REVIEW_STATUS_AFTER_APPEAL_SUBMIT)
      this.appealVisible = false
      this.refreshReviewListAfterMutation()
      const taskId =
        (row && row.taskId) ||
        (row && row.task && row.task.taskId) ||
        ''
      this.$Message.success(
        taskId
          ? `申诉已受理，任务 ID：${taskId}`
          : '申诉已提交，请等待 TT 反馈'
      )
    },
    canInitiateAppeal,
    handleViewRecords(row) {
      this.reviewRecordRow = { ...row }
      this.reviewRecordVisible = true
    },
    /** 确认送审成功后，审核记录里「修改送审资料 / 发起申诉」置灰 */
    markReviewRecordActionsDisabled(dramaId) {
      if (dramaId == null) return
      this.$set(this.reviewRecordActionsUsedMap, dramaId, true)
    },
    handleProfileSubmitSuccess() {
      const id = this.profileDialogRow && this.profileDialogRow.id
      this.markReviewRecordActionsDisabled(id)
      this.profileDefaultEditMode = false
      this.profileDialogVisible = false
      this.refreshReviewListAfterMutation()
    },
    handleWithdrawSuccess() {
      const id = this.reviewRecordRow && this.reviewRecordRow.id
      if (id != null) {
        this.markReviewRecordActionsDisabled(id)
      }
      this.refreshReviewListAfterMutation()
    },
    handleReviewRecordAppealSuccess(row) {
      const id = row && row.id
      this.markReviewRecordActionsDisabled(id)
    },
    handleReviewRecordModifySubmit(row) {
      this.profileDialogRow = { ...row }
      this.profileDefaultEditMode = true
      this.profileDialogVisible = true
    },
    handlePreview(row) {
      this.previewDialogRow = { ...row }
      this.previewDialogVisible = true
    }
  }
}
</script>

<style scoped>
.short-review-table {
  width: 100%;
}

/* 在 iView 默认单元格上下 padding 10px 基础上各 +4px，行高共 +8px */
.short-review-table >>> .ivu-table td {
  vertical-align: middle;
}

.short-review-table >>> .ivu-table-tbody .ivu-table-cell {
  padding-top: 14px;
  padding-bottom: 14px;
  font-size: 14px;
}

.short-review-table >>> .ivu-table-header .ivu-table-cell {
  font-size: 14px;
}

.review-tip-bar {
  margin-top: 4px;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.5;
  color: #606266;
  background: #f8f8f9;
  border-left: 3px solid #409eff;
  border-radius: 2px;
}

.drama-info-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.drama-cover img,
.drama-cover-placeholder {
  width: 56px;
  height: 74px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
}

.drama-cover img {
  cursor: pointer;
}

.drama-cover-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  font-size: 13px;
  color: #999;
}

.drama-title-en {
  font-size: 15px;
  color: #303133;
  line-height: 1.4;
}

.drama-title-cn {
  margin-top: 4px;
  font-size: 13px;
  color: #909399;
  line-height: 1.4;
}

.bp-sync-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
}

.bp-sync-line {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 22px;
}

.bp-sync-text {
  font-size: 14px;
  color: #303133;
}

.bp-sync-fail-help {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: #f56c6c;
  cursor: pointer;
  border-radius: 2px;
  transition: background-color 0.15s ease;
  user-select: none;
}

.bp-sync-fail-help:hover {
  background-color: #fde2e2;
  color: #f56c6c;
}

/* 时间与状态文案左对齐，不参与圆点垂直居中 */
.bp-sync-time {
  margin-top: 4px;
  margin-left: 18px;
}

.status-block {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  max-width: 100%;
}

.review-status-badge {
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot--green {
  background: #67c23a;
}

.status-dot--blue {
  background: #409eff;
}

.status-dot--gray {
  background: #c0c4cc;
}

.status-dot--red {
  background: #f56c6c;
}

.status-dot--orange {
  background: #e6a23c;
}

/* 未送审：送审条件校验不通过原因（图二） */
.review-submit-missing-hint {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: #b38a58;
  word-break: break-word;
}

.status-time {
  margin-top: 4px;
  font-size: 13px;
  color: #999;
  line-height: 1.2;
}

.action-links {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
}

.action-links a {
  color: #2d8cf0;
  white-space: nowrap;
}
</style>

